import {
  BenefitSupplierType as ApiBenefitSupplierType,
  EmployeeBenefitDocumentDto,
  EmployeeBenefitTransactionDto,
} from '@flexyoo/api';
import {
  ApplicationPlanType,
  BenefitSupplierType,
  EmployeeBenefitDocumentType,
} from '@flexyoo/api/models';
import { EmployeeBenefitDocument } from '@flexyoo/employee/models';
import {
  findEmployeeBenefitDocument,
  getDocumentFileName,
  getDocumentState,
  isDocumentApproved,
  isNeedToResign,
  mapDocumentStateToAddendumStatus,
} from '@flexyoo/employee/util';
import { STATUS_MAP } from '@flexyoo/models';
import { ITransaction } from '../models';

export const mapEmployeeBenefitTransactionModel: (
  model: EmployeeBenefitTransactionDto
) => ITransaction = ({ Benefit, Employee }: EmployeeBenefitTransactionDto) => {
  const isBenefitNotApprovedByExternalProvider: boolean =
    [
      ApiBenefitSupplierType.ANM,
      ApiBenefitSupplierType.O2O,
      ApiBenefitSupplierType.Joule,
      ApiBenefitSupplierType.LeaseABike,
      ApiBenefitSupplierType.VandenBorre,
    ].includes(Benefit.SupplierType) &&
    STATUS_MAP[Benefit.Status] === 'hr-approved' &&
    !Benefit.IsConfirmationSentToExternalProvider &&
    !!Benefit.ExternalProviderConfirmationErrorMessage;

  const documents = mapEmployeeBenefitDocuments(Benefit.EmployeeBenefitDocuments);
  const addendumDocument = findEmployeeBenefitDocument(
    documents,
    EmployeeBenefitDocumentType.Addendum
  );
  const offerDocument = findEmployeeBenefitDocument(documents, EmployeeBenefitDocumentType.Offer);
  const addendumState = getDocumentState(
    documents,
    EmployeeBenefitDocumentType.Addendum,
    Benefit.AddendumDisapprovedReason
  );
  const addendumFileName = getDocumentFileName(documents, EmployeeBenefitDocumentType.Addendum);
  const offerFileName = getDocumentFileName(documents, EmployeeBenefitDocumentType.Offer);

  const transaction: ITransaction = {
    id: `${Employee.Id}_${Benefit.Id}`,
    category: Benefit.Category,
    tenantName: Employee.TenantName ?? '',
    date: new Date(Benefit.Date),
    employeeExternalId: Employee.ExternalId,
    employeeFirstName: Employee.FirstName ?? '',
    employeeLastName: Employee.LastName ?? '',
    tenantGroupBenefitId: Benefit.TenantBenefitChildId ?? 0,
    categoryNameEn: Benefit.CategoryNameEn,
    categoryNameFr: Benefit.CategoryNameFr,
    categoryNameNl: Benefit.CategoryNameNl,
    name: Benefit.DisplayName ?? Benefit.Name ?? '',
    annualBudget: Benefit.YearlyGrossPrice,
    yearlyAmountNet: Benefit.YearlyNetPrice,
    additionalLeaveDaysCount: Benefit.QuantityOfLeaveDays ?? 0,
    totalBudget: Benefit.TotalGrossPrice,
    totalSSBudget: Benefit.TotalGrossSSAmount ?? 0,
    advantageDuration: Benefit.DurationInMonths ?? 0,
    residualValue: Benefit.ResidualValue,
    annualResidualValue: Benefit.AnnualResidualValue,
    isResidualFlexEndDatePassed: Benefit.IsFlexEndDateReached,
    isResidualValuePaid: Benefit.IsResidualValuePaid,
    isResidualValueDefined: Benefit.IsResidualValueDefined,
    advantageEndDate: Benefit.EndDate ? new Date(Benefit.EndDate) : new Date(Benefit.Date),
    status: STATUS_MAP[Benefit.Status],
    addendum: {
      status: mapDocumentStateToAddendumStatus(addendumState),
      fileName: addendumFileName,
      documentId: addendumDocument?.Id,
      appFileId: addendumDocument?.AppFileId,
      isAzureFile: addendumDocument?.IsAzureFile,
      needToResignSignature: isNeedToResign(
        documents,
        EmployeeBenefitDocumentType.Addendum,
        Benefit.AddendumDisapprovedReason
      ),
      disapprovedReason: Benefit.AddendumDisapprovedReason,
    },
    period: Benefit.Period ?? '',
    employeeId: Employee.Id,
    benefitId: Benefit.Id,
    isPayrollSubmitted: Benefit.IsPayrollSubmitted,
    isTenantYearClosed: Benefit.IsTenantYearClosed,
    addendumFileName,
    personalOfferFileName: offerFileName,
    personalOfferDocumentId: offerDocument?.Id,
    personalOfferAppFileId: offerDocument?.AppFileId,
    personalOfferIsAzureFile: offerDocument?.IsAzureFile,
    isPersonalOfferApproved: isDocumentApproved(documents, EmployeeBenefitDocumentType.Offer),
    needToResignPersonalOffer: isNeedToResign(
      documents,
      EmployeeBenefitDocumentType.Offer,
      Benefit.OfferDisapprovedReason
    ),
    offerDisapprovedReason: Benefit.OfferDisapprovedReason,
    year: Benefit.Year,
    canBeCanceled: Benefit.CanBeCancelled,
    isAnmBenefit: Benefit.IsAnmBenefit,
    bonusPlus: Benefit.PaymentIndicator?.BonusPlusAmount ?? 0,
    bonusMinus: Benefit.PaymentIndicator?.BonusMinusAmount ?? 0,
    p13: Benefit.PaymentIndicator?.P13Amount ?? 0,
    blended: Benefit.PaymentIndicator?.BlendedAmount ?? 0,
    direct: Benefit.PaymentIndicator?.DirectAmount ?? 0,
    isImported: Benefit.IsImported,
    supplierType: Benefit.SupplierType as unknown as BenefitSupplierType,
    tenantBenefitChildId: Benefit.TenantBenefitChildId,
    isBenefitNotApprovedByExternalProvider,
    applicationPlanType: Benefit.ApplicationPlanType as unknown as ApplicationPlanType,
    subscriptionNumber: Benefit.SubscriptionNumber,
    yearlySSamount: Benefit.YearlyGrossSSAmount,
    monthlySSamount: Benefit.MonthlyGrossSSAmount,
    tenantId: Benefit.TenantId,
  };

  return transaction;
};

function mapEmployeeBenefitDocuments(
  documents: EmployeeBenefitDocumentDto[] | undefined
): EmployeeBenefitDocument[] {
  return (documents ?? []).map((document) => ({
    Id: document.Id,
    DocumentType: document.DocumentType as unknown as EmployeeBenefitDocumentType,
    DisplayName: document.DisplayName ?? '',
    AppFileId: document.AppFileId,
    ApprovedDate: document.ApprovedDate ?? null,
    RoleOfUserOnUpload: document.RoleOfUserOnUpload ?? '',
    UploaderUserId: document.UploaderUserId ?? '',
    CreatedDate: document.CreatedDate,
    ModifiedDate: document.ModifiedDate,
    AppFileName: document.AppFileName,
    IsAzureFile: document.IsAzureFile,
  }));
}
